import { useCallback, useEffect, useRef, useState } from 'react'
import { analyticsErrorMessage } from './format'

export interface RemoteState<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
  /** Ключ показанного успешного ответа; null, пока текущий срез не получен. */
  dataKey: string | null
}

/**
 * Загрузка данных страницы по ключу. Ключ — строка (обычно JSON запроса): при его смене запрос
 * повторяется, а ответ устаревшего запроса отбрасывается. При обновлении того же среза данные
 * остаются на экране. При смене ключа прежний ответ нельзя выдавать под новыми подписями/ссылками.
 */
export function useRemote<T>(
  key: string,
  fetcher: () => Promise<T>,
  fallbackMessage: string,
  enabled = true,
): RemoteState<T> {
  const [result, setResult] = useState<{ key: string; value: T } | null>(null)
  const [request, setRequest] = useState<{ key: string; loading: boolean; error: string | null } | null>(null)
  const [version, setVersion] = useState(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    if (!enabled) {
      return
    }
    let cancelled = false
    setRequest({ key, loading: true, error: null })
    fetcherRef
      .current()
      .then((value) => {
        if (cancelled) return
        setResult({ key, value })
        setRequest({ key, loading: false, error: null })
      })
      .catch((reason) => {
        if (cancelled) return
        setRequest({ key, loading: false, error: analyticsErrorMessage(reason, fallbackMessage) })
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version, enabled])

  const reload = useCallback(() => setVersion((value) => value + 1), [])
  // Проверка в render, а не сброс useEffect: даже первый кадр нового среза безопасен.
  const matches = result?.key === key
  return {
    data: matches ? result.value : null,
    dataKey: matches ? key : null,
    error: request?.key === key ? request.error : null,
    loading: enabled && (request?.key !== key || request.loading),
    reload,
  }
}
